// ===================== AdminLogin.jsx =====================
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, User, Mail } from "lucide-react";
import axios from "axios";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);
  const [adminName, setAdminName] = useState("");

  const navigate = useNavigate();

  /* ====================================
       BACK BUTTON TO CLOSE POPUP
  ==================================== */
  useEffect(() => {
    const handler = () => {
      const danger = document.getElementById("danger-overlay");
      if (danger) danger.remove();
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  /* ===========================
     🔥 ULTRA DANGER ALERT SYSTEM
  ============================ */
  const triggerDangerAlert = () => {
    const alarmSound = new Audio("/alarm.mp3");
    alarmSound.volume = 1.0;
    alarmSound.play().catch(() => {});

    const alertBox = document.createElement("div");
    alertBox.innerHTML = `
      <div id="danger-overlay" style="
        position: fixed;
        inset: 0;
        background: black;
        z-index: 999999999;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: flashRed 0.2s infinite;
      ">
        <div style="
          text-align: center;
          color: #ff0000;
          animation: shake 0.25s infinite;
        ">
          <div style="font-size: 110px; margin-bottom: 10px;">🚨</div>

          <h1 style="
            font-size: 36px;
            font-weight: bold;
            text-shadow: 0 0 20px red;
          ">
            SECURITY BREACH DETECTED!
          </h1>

          <p style="
            margin-top: 15px;
            font-size: 20px;
            color: #ffb3b3;
            text-shadow: 0 0 10px red;
            line-height: 1.5;
          ">
            Wrong Admin Password Attempt! <br/>
            Alarm Activated! Screen Locked!
          </p>

          <!-- ❌ BACK BUTTON REMOVED -->
        </div>
      </div>

      <style>
        @keyframes flashRed {
          0% { background-color: rgba(255,0,0,0.1); }
          50% { background-color: rgba(255,0,0,0.7); }
          100% { background-color: rgba(255,0,0,0.1); }
        }
        @keyframes shake {
          0% { transform: translate(0px); }
          25% { transform: translate(-6px); }
          50% { transform: translate(6px); }
          75% { transform: translate(-4px); }
          100% { transform: translate(0px); }
        }
      </style>
    `;

    document.body.appendChild(alertBox);

    // USER BACK চাপলে popup বন্ধ হবে
    window.history.pushState(null, "");
  };

  /* ===========================
       LOGIN SUBMIT
  ============================ */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(
        "https://api.speedfollowinfo.com/api/auth/admin/login",
        { email, username, password }
      );

      if (res.data.success) {
        localStorage.setItem("adminToken", res.data.token);
        localStorage.setItem("admin", JSON.stringify({ username, email }));

        setAdminName(username);
        setShowSuccess(true);

        setTimeout(() => navigate("/admin"), 1700);
      }
    } catch (err) {
      triggerDangerAlert();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0f1a] relative overflow-hidden">

      {/* SUCCESS POPUP */}
      {showSuccess && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-xl z-[99999] animate-fadeIn">
          <div className="bg-white/10 backdrop-blur-2xl p-10 rounded-3xl border border-white/20 shadow-2xl text-center animate-scaleIn">

            <div className="text-6xl mb-3 animate-pop">🛡️</div>

            <h2 className="text-3xl font-extrabold text-[#47ffda] drop-shadow-lg">
              Admin Verified
            </h2>

            <p className="text-lg text-white mt-2">
              Welcome, <span className="text-[#00eaff]">{adminName}</span>
            </p>

            <p className="text-gray-300 mt-2">Loading Admin Panel...</p>
          </div>

          <style>{`
            @keyframes fadeIn { from{opacity:0;} to{opacity:1;} }
            @keyframes scaleIn {
              0%{ transform:scale(0.7); opacity:0; }
              100%{ transform:scale(1); opacity:1; }
            }
            @keyframes pop {
              0%{ transform:scale(0.5); }
              80%{ transform:scale(1.2); }
              100%{ transform:scale(1); }
            }
            .animate-scaleIn { animation: scaleIn .3s ease-out; }
            .animate-pop { animation: pop .45s ease-out; }
          `}</style>
        </div>
      )}

      {/* BACKGROUND LIGHTS */}
      <div className="absolute w-[600px] h-[600px] bg-[#1da1f2]/20 blur-3xl rounded-full top-[-100px] right-[-150px] animate-pulse"></div>
      <div className="absolute w-[500px] h-[500px] bg-[#6c63ff]/20 blur-3xl rounded-full bottom-[-150px] left-[-150px] animate-pulse"></div>

      {/* LOGIN BOX */}
      <form
        onSubmit={handleLogin}
        className="relative bg-[#111827]/80 backdrop-blur-xl p-8 rounded-2xl shadow-2xl w-[90%] sm:w-[400px] border border-[#1da1f2]/40"
      >
        <h1 className="text-2xl font-bold text-center mb-2">
          Admin <span className="text-[#1da1f2]">Login</span>
        </h1>

        <p className="text-center text-gray-400 mb-6">Secure Access 🔐</p>

        {/* EMAIL */}
        <div className="mb-4">
          <label className="text-gray-300 text-sm">Email</label>
          <div className="flex items-center bg-[#1a2035] border border-[#1da1f2]/40 rounded-lg px-3 py-2">
            <Mail size={18} className="text-[#1da1f2] mr-2" />
            <input
              type="email"
              placeholder="Admin email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent text-white flex-1 outline-none"
              required
            />
          </div>
        </div>

        {/* USERNAME */}
        <div className="mb-4">
          <label className="text-gray-300 text-sm">Username</label>
          <div className="flex items-center bg-[#1a2035] border border-[#1da1f2]/40 rounded-lg px-3 py-2">
            <User size={18} className="text-[#1da1f2] mr-2" />
            <input
              type="text"
              placeholder="Admin username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-transparent text-white flex-1 outline-none"
              required
            />
          </div>
        </div>

        {/* PASSWORD */}
        <div className="mb-4">
          <label className="text-gray-300 text-sm">Password</label>
          <div className="flex items-center bg-[#1a2035] border border-[#1da1f2]/40 rounded-lg px-3 py-2">
            <Lock size={18} className="text-[#1da1f2] mr-2" />

            <input
              type={showPass ? "text" : "password"}
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-transparent text-white flex-1 outline-none"
              required
            />

            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="text-gray-400 text-xs"
            >
              {showPass ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {/* SUBMIT */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-[#1da1f2] to-[#6c63ff] hover:shadow-[0_0_15px_#1da1f2] text-white py-2 rounded-lg font-semibold transition"
        >
          {loading ? "Checking..." : "Login"}
        </button>
      </form>
    </div>
  );
}